import { Link } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { Plane, Shield, Clock, Users, Star, MapPin, Calendar, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { usePackages } from '../hooks/usePackages'

const features = [
  {
    icon: Plane,
    title: 'Best Flight Deals',
    description: 'Compare prices from multiple airlines and find the best deals'
  },
  {
    icon: Shield,
    title: 'Secure Bookings',
    description: 'Safe and secure flight booking with instant confirmations'
  },
  {
    icon: Clock,
    title: '24/7 Support',
    description: 'Round-the-clock customer service for all your travel needs'
  },
  {
    icon: Users,
    title: 'Group Bookings',
    description: 'Special rates for group travel and corporate bookings'
  }
]

export default function Flights() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const {
    packages,
    pagination,
    loading: packagesLoading,
    error,
    loadPage,
    goToNextPage,
    goToPrevPage,
  } = usePackages({ initialFilters: { offer_type: 1, per_page: 12 }, initialPage: 1 })

  const loading = franchiseLoading || packagesLoading

  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) return pkg.cover_photo.large_url
    if (pkg.cover_photo?.url) return pkg.cover_photo.url
    if (pkg.scraped_cover_photo) return pkg.scraped_cover_photo
    return null
  }

  // Best-effort auto-resize for the Travelstart iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      try {
        const data = event.data
        if (!data) return
        // Handle common postMessage formats
        if (typeof data === 'string') {
          // Example patterns: "setHeight:1234" or "ts-iframe-height:1234"
          const match = data.match(/(setHeight|iframeHeight|ts-iframe-height)\s*:?\s*(\d{3,5})/i)
          if (match && iframeRef.current) {
            const h = parseInt(match[2], 10)
            if (!Number.isNaN(h) && h > 300) {
              iframeRef.current.style.height = `${h}px`
            }
          }
        } else if (typeof data === 'object' && 'height' in data) {
          const h = (data as any).height
          if (iframeRef.current && typeof h === 'number' && h > 300) {
            iframeRef.current.style.height = `${h}px`
          }
        }
      } catch (_) {
        // ignore
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  if (loading && !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Something went wrong</h2>
            <p className="mt-6 text-lg leading-8 text-red-600">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-8 btn-primary">Try Again</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('/Flysafair-plane-header.jpg')`
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Flight Bookings
          </h1>
          <p className="text-lg sm:text-xl text-white mb-8 max-w-2xl mx-auto">
            Book domestic and international flights with ease. Compare prices, find the best deals, and travel with confidence.
          </p>
        </div>
      </div>

      {/* Travelstart White Label Integration Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 lg:p-12 shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Search & Book Flights
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Use our integrated flight search to find and book the best flight deals
              </p>
            </div>
            
            {/* Travelstart White Label Integration Point */}
            <div className="bg-white rounded-xl p-6 lg:p-8 shadow-md">
              <div id="travelstart-widget">
                <iframe
                  ref={iframeRef}
                  id="travelstartIframe-39daee2d-b6c4-4404-bbbc-efabeb5045db"
                  frameBorder="0"
                  scrolling="auto"
                  style={{ margin: '0px', padding: '0px', border: '0px', backgroundColor: '#FAFAFA', width: '100%', height: '850px' }}
                  src="https://www.travelstart.co.za/?search=false&show_banners=false&log=false&affId=5799442&language=&affCampaign=&cpy_source=tszaweb&utm_source=affiliate&utm_medium=5799442&isiframe=true&landing_page=false&iframeVersion=11&host=https://starsintransition.com/services/flights"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Book Flights With Us?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We partner with leading airlines and flight booking platforms to offer you the best deals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flight Packages Section removed as requested */}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Book Your Flight?
          </h2>
          <p className="text-xl text-white mb-8">
            Let us help you find the perfect flight with personalized service and expert recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-purple-600 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition"
            >
              Contact Us
            </Link>
            <Link
              to="/packages"
              className="bg-transparent border border-white text-white font-semibold px-8 py-3 rounded-lg hover:bg-white hover:text-purple-600 transition"
            >
              View Packages
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

